/**
 * Auto-translation layer.
 *
 * Every JSX text node and a whitelist of text-bearing props are routed through
 * `autoTranslate()` by the JSX runtime shim (src/i18n/jsx-runtime.ts). The
 * dictionaries below are keyed by the exact English source string, and are
 * generated by `bun scripts/i18n-extract.ts` + `bun scripts/i18n-translate.ts`.
 *
 * English is always a pass-through, so the English pages are byte-identical to
 * what the components render — and, crucially, English visitors never download
 * a single byte of translation data. Each non-English dictionary lives in its
 * own lazily fetched chunk.
 */
import type { Locale } from "./config";

type Dict = Record<string, string>;

const LOADERS = import.meta.glob<{ default: Dict }>("./auto/*.json");

const DICTS: Partial<Record<Locale, Dict>> = {};
const PENDING: Partial<Record<Locale, Promise<void>>> = {};

function loaderFor(locale: Locale) {
  return LOADERS[`./auto/${locale}.json`];
}

export function isAutoDictReady(locale: Locale): boolean {
  return locale === "en" || Boolean(DICTS[locale]) || !loaderFor(locale);
}

/** Load (once) the dictionary chunk for a locale. */
export function ensureAutoDict(locale: Locale): Promise<void> {
  if (isAutoDictReady(locale)) return Promise.resolve();
  const existing = PENDING[locale];
  if (existing) return existing;
  const load = loaderFor(locale)!;
  const p = load().then((mod) => {
    DICTS[locale] = mod.default;
  });
  PENDING[locale] = p;
  return p;
}

// On the server (SSR + prerender) rendering is synchronous, so every dictionary
// is resolved up front. This never reaches the browser bundle.
if (import.meta.env.SSR) {
  await Promise.all(
    Object.keys(LOADERS).map((path) => {
      const locale = path.slice("./auto/".length, -".json".length) as Locale;
      return ensureAutoDict(locale);
    }),
  );
}

let current: Locale = "en";

/** Set by the root layout on every render / navigation. */
export function setAutoLocale(locale: Locale) {
  current = locale;
}

export function getAutoLocale(): Locale {
  return current;
}

export function autoTranslateFor(locale: Locale, input: string): string {
  if (locale === "en") return input;
  const dict = DICTS[locale];
  if (!dict) return input;
  const key = input.trim();
  if (key.length < 2) return input;
  const hit = dict[key];
  if (!hit) return input;
  // Preserve any leading/trailing whitespace the JSX transform kept.
  return input === key ? hit : input.replace(key, hit);
}

export function autoTranslate(input: string): string {
  return autoTranslateFor(current, input);
}
